import { Inject, Injectable } from "@angular/core";
import type { Observable } from "rxjs";
import type {
  BranchApiResponse,
  BranchCreateRequest,
  BranchListQuery,
  BranchListResponse,
  BranchSelectionQuery,
  BranchSelectionResponse,
  BranchUpdateRequest,
  GeocodingApiResponse,
} from "../models/branches/branch.model";
import type { IEnvironment } from "../models/interfaces/environment.interface";
import { ENVIRONMENT } from "../modules/keycloak.module";
import { normalizeDniWithDash } from "../utils/validators.util";
import { HttpParamsUtil } from "../utils/http-params.util";
import { RetryHttpClient } from "./base/retryable-http.client";

@Injectable({ providedIn: "root" })
export class BranchService extends HttpParamsUtil {
  private readonly resourceUrl: string;

  constructor(
    private readonly httpClient: RetryHttpClient,
    @Inject(ENVIRONMENT) environment: IEnvironment
  ) {
    super();
    this.resourceUrl = `${environment.BASE_URL}/v1/parameters/providers/technical-branches`;
  }

  getBranches(query: BranchListQuery): Observable<BranchListResponse> {
    const params = this.toHttpParams({
      provider_dni: query.providerDni,
      provider_name: query.providerName,
      branch_name: query.branchName,
      branch_id: query.branchId,
      external_id: query.externalId,
      page: query.page ?? 1,
      page_size: query.pageSize ?? 10,
    });

    return this.httpClient.get<BranchListResponse>(this.resourceUrl, { params });
  }

  getAllBranches(page = 1, pageSize = 10): Observable<BranchListResponse> {
    const params = this.toHttpParams({
      page,
      page_size: pageSize,
    });

    return this.httpClient.get<BranchListResponse>(this.resourceUrl, { params });
  }

  selectBranches(query: BranchSelectionQuery): Observable<BranchSelectionResponse> {
    const params = this.toHttpParams({
      provider_dni: normalizeDniWithDash(query.providerDni),
      brand: query.brand,
      category: query.category,
      sub_category: query.subCategory,
      warranty: query.warranty,
      lat: query.lat,
      lon: query.lon,
    });

    return this.httpClient.get<BranchSelectionResponse>(`${this.resourceUrl}/select`, { params });
  }

  geocodeAddress(address: string): Observable<GeocodingApiResponse> {
    const params = this.toHttpParams({ address });

    return this.httpClient.get<GeocodingApiResponse>(`${this.resourceUrl}/geocoding`, { params });
  }

  getBranch(id: string): Observable<BranchApiResponse> {
    return this.httpClient.get<BranchApiResponse>(`${this.resourceUrl}/${id}`);
  }

  createBranch(request: BranchCreateRequest): Observable<BranchApiResponse> {
    return this.httpClient.post<BranchCreateRequest, BranchApiResponse>(this.resourceUrl, request);
  }

  updateBranch(id: string, request: BranchUpdateRequest): Observable<BranchApiResponse> {
    return this.httpClient.patch<BranchUpdateRequest, BranchApiResponse>(`${this.resourceUrl}/${id}`, request);
  }

  deleteBranch(id: string): Observable<void> {
    return this.httpClient.request<never, void>("DELETE", `${this.resourceUrl}/${id}`);
  }
}
